import { SystemRole } from 'shared/src/authz/rolesV2';

/**
 * Categories for organizing system roles
 */
export enum RoleCategory {
  ADMINISTRATIVE = 'administrative',
  STANDARD = 'standard',
  OPERATIONAL = 'operational',
  SUPPORT = 'support',
}

/**
 * Display names for system roles
 */
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  [SystemRole.ADMIN]: 'Admin',
  [SystemRole.ADMIN_READER]: 'Admin Reader',
  [SystemRole.ENTITLEMENTS_ADMIN]: 'Entitlements Admin',
  [SystemRole.ENTITLEMENTS_READER]: 'Entitlements Reader',
  [SystemRole.DEVELOPER]: 'Developer',
  [SystemRole.SE]: 'SE',
  [SystemRole.BILLING_READER]: 'Billing Reader',
  [SystemRole.CICD]: 'CI/CD',
  [SystemRole.PURGE]: 'Purge',
  [SystemRole.SUPPORT]: 'Support',
  [SystemRole.SMTP_ADMIN]: 'SMTP Admin',
  [SystemRole.USER]: 'User',
};

/**
 * Format role names for UI display
 */
export const formatSystemRole = (role: string): string => ROLE_DISPLAY_NAMES[role] || role;

/**
 * Role descriptions for UI display
 */
export const ROLE_DESCRIPTIONS: Record<string, string> = {
  [SystemRole.ADMIN]: 'Full system access and administration',
  [SystemRole.ADMIN_READER]: 'Read-only system administration access',
  [SystemRole.ENTITLEMENTS_ADMIN]: 'Full entitlements administration access',
  [SystemRole.ENTITLEMENTS_READER]: 'Read-only entitlements and usage access',
  [SystemRole.DEVELOPER]: 'Development and testing access',
  [SystemRole.SE]: 'Solutions Engineer support access',
  [SystemRole.BILLING_READER]: 'Billing and usage reporting access',
  [SystemRole.CICD]: 'CI/CD pipeline management access',
  [SystemRole.PURGE]: 'Data purging operations access',
  [SystemRole.SUPPORT]: 'Customer support access',
  [SystemRole.SMTP_ADMIN]: 'SMTP configuration management',
  [SystemRole.USER]: 'Standard user access',
};

/**
 * Role categories for grouping in UI
 */
export const ROLE_CATEGORIES: Record<RoleCategory, SystemRole[]> = {
  [RoleCategory.ADMINISTRATIVE]: [SystemRole.ADMIN, SystemRole.ADMIN_READER],
  [RoleCategory.OPERATIONAL]: [SystemRole.PURGE, SystemRole.SMTP_ADMIN, SystemRole.CICD],
  [RoleCategory.SUPPORT]: [
    SystemRole.SE,
    SystemRole.BILLING_READER,
    SystemRole.ENTITLEMENTS_ADMIN,
    SystemRole.ENTITLEMENTS_READER,
    SystemRole.SUPPORT,
    SystemRole.SMTP_ADMIN,
  ],
  [RoleCategory.STANDARD]: [SystemRole.DEVELOPER, SystemRole.USER],
};

/**
 * Color constants for role status indicators in dropdown
 */
export const ROLE_INDICATOR_COLORS = {
  ASSIGNED: '#52C41A', // Green for assigned roles
  UNASSIGNED: '#FF4D4F', // Red for unassigned roles
};

/**
 * Icon constants for role status indicators in dropdown
 */
export const ROLE_INDICATOR_ICONS = {
  ASSIGNED: '✓',
  UNASSIGNED: '✗',
};

/**
 * Maximum number of roles to show in tooltip before "X more"
 */
const TOOLTIP_ROLE_LIMIT = 3;

/**
 * Create tooltip content for system roles (truncated to limit roles)
 */
export const buildSystemRolesTooltip = (roles: SystemRole[]): string => {
  const displayRoles = roles.slice(0, TOOLTIP_ROLE_LIMIT).map((r) => formatSystemRole(r));
  const hasMore = roles.length > TOOLTIP_ROLE_LIMIT;

  const roleList = displayRoles.join('\n');
  const moreText = hasMore ? `\n+ ${roles.length - TOOLTIP_ROLE_LIMIT} more` : '';

  return roleList + moreText;
};
